import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@wundo/ui';
import type { ConversationDebugPayload } from '@/lib/conversationDebug.ts';
import { AnalyserSessions } from './debug/AnalyserSessions.tsx';
import { CopyButton } from './debug/CopyButton.tsx';
import { DebugSection } from './debug/DebugSection.tsx';
import { IdentitySection } from './debug/IdentitySection.tsx';
import { OrchestratorSection } from './debug/OrchestratorSection.tsx';
import { RoleplaySection } from './debug/RoleplaySection.tsx';
import { TimingSection } from './debug/TimingSection.tsx';
import { useConversationDebug } from './debug/useConversationDebug.ts';

export interface ConversationDebugDialogProps {
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Everything the backend knows about one conversation: what it is, what it is
 * made of, what the orchestrator was doing, what the analyser made of it — and
 * the links out to the trace that produced it.
 */
export function ConversationDebugDialog({
  conversationId,
  open,
  onOpenChange,
}: ConversationDebugDialogProps) {
  const { debug, data } = useConversationDebug(conversationId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='lg' layout='scroll' className='p-0'>
        <DialogHeader>
          <DialogTitle>Inspect conversation</DialogTitle>
          <DialogClose />
        </DialogHeader>

        <div className='flex flex-col gap-6 px-5 pt-4 pb-5 text-sm'>
          {debug.isPending && <Spinner className='size-5' />}
          {debug.isError && (
            <p role='alert' className='text-sm font-semibold text-danger'>
              Failed to load conversation debug info
            </p>
          )}

          {data && (
            <>
              <IdentitySection data={data} />
              <TimingSection data={data} />
              <OrchestratorSection data={data} />
              {data.roleplay_session && (
                <RoleplaySection roleplay={data.roleplay_session} />
              )}

              <DebugSection title='Analyser'>
                <AnalyserSessions
                  data={data}
                  loadingMore={debug.isFetchingNextPage}
                  onLoadMore={() => void debug.fetchNextPage()}
                />
              </DebugSection>

              <DebugSection title='Links'>
                <ul className='flex flex-col gap-1'>
                  <li>
                    <Link
                      to='/users/$id'
                      params={{ id: data.user.id }}
                      className='inline-flex items-center gap-1 text-sm text-ink-secondary underline underline-offset-2 hover:text-ink'
                    >
                      <ExternalLink className='size-3.5' aria-hidden='true' />
                      User detail
                    </Link>
                  </li>
                </ul>
              </DebugSection>

              <RawPayload data={data} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The answer exactly as the backend gave it, to copy into a bug report. */
function RawPayload({ data }: { data: ConversationDebugPayload }) {
  return (
    <Accordion>
      <AccordionItem>
        <AccordionTrigger className='px-3.5 py-2.5 text-sm'>Raw</AccordionTrigger>
        <AccordionPanel className='px-3.5 pb-3'>
          <div className='flex justify-end'>
            <CopyButton value={JSON.stringify(data, null, 2)} label='Copy raw payload' />
          </div>
          <pre className='mt-1 overflow-x-auto rounded-sm bg-fill-faint p-2 text-xs whitespace-pre-wrap text-ink-body'>
            {JSON.stringify(data, null, 2)}
          </pre>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
